/** Pen weights — AutoCAD's lineweights, as stroke widths.
 *
 * A module of its own rather than a helper inside `Canvas2D`, for the reason every other pure
 * converter here is: the canvas cannot be imported by `node --test` without a DOM, and a rule
 * this small deserves a test that runs in milliseconds.
 */

/** A lineweight, in hundredths of a millimetre, as a stroke width in CSS pixels.
 *
 *  ⚠⚠ **IT DOES NOT SCALE WITH ZOOM, and that is AutoCAD's behaviour, not a shortcut.** A pen
 *  weight is what the PLOTTER will lay down; on screen AutoCAD draws it at a fixed width so a
 *  drafter can see which lines are heavy without zooming to a particular scale. Multiplying it
 *  by the view scale would make a 0.50 mm border a hairline zoomed out and a slab zoomed in.
 *
 *  ⚠ 96 dpi — 3.7795 px per millimetre. So 0.25 mm draws just under a pixel and 2.11 mm draws
 *  eight, and everything thinner than about 0.27 mm floors to the hairline this canvas already
 *  drew. That is the honest conversion; a fatter one would make the picture prettier and the
 *  widths wrong, and a drafter reads these to know what will plot.
 *
 *  ⚠ -1, -2 and -3 are BYLAYER, BYBLOCK and DEFAULT. None of them is a width, and all of them
 *  reach here only when nothing resolved, so they draw as the hairline rather than as an
 *  invented one. `display.py` resolves -3 to LWDEFAULT before it ever gets this far.
 */
export function penWidth(lw: number): number {
  return lw > 0 ? Math.max(1, lw * 0.037795) : 1;
}

/** AutoCAD's twenty-four plottable widths, in hundredths of a millimetre.
 *  ⚠ Not a range — the pen table has these and only these. Mirrors `sysvars.LINEWEIGHTS`. */
export const LINEWEIGHTS: readonly number[] = [
  0, 5, 9, 13, 15, 18, 20, 25, 30, 35, 40, 50, 53, 60, 70, 80, 90, 100,
  106, 120, 140, 158, 200, 211,
];
